import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { ArrowLeft, Save, Heart, Brain, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const GRUNDERKRANKUNGEN = ['Diabetes mellitus Typ 1', 'Diabetes mellitus Typ 2', 'pAVK', 'CVI', 'Herzinsuffizienz', 'Niereninsuffizienz', 'Immunsuppression', 'Tumorerkrankung', 'Mischformen'];
const REZIDIV_RISIKEN = ['Immobilität', 'Inkontinenz', 'Durchblutungsstörungen', 'Malnutrition', 'Rauchen', 'Adipositas', 'Hohes Alter', 'Sensibilitätsstörung'];

export default function AnamnesePage() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    patient_id: patientId,
    grunderkrankungen: [], grunderkrankungen_sonstige: '',
    ernaehrungszustand_bmi: 0, ernaehrungszustand_gewicht: 0, ernaehrungszustand_groesse: 0,
    mobilitaet: '', allergien: '', medikation: '',
    psychosozial_lebensqualitaet: '', psychosozial_koerperbild: '',
    psychosozial_unterstuetzung: '', psychosozial_krankheitsverstaendnis: '',
    psychosozial_selbstmanagement: '',
    rezidiv_risikofaktoren: [], rezidiv_sonstige: ''
  });

  useEffect(() => { loadAnamnese(); }, [patientId]);

  const loadAnamnese = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wunddok_token');
      const res = await axios.get(`${API}/api/anamnese/${patientId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.patient_id) setForm(prev => ({ ...prev, ...res.data }));
    } catch { /* new anamnese */ }
    finally { setLoading(false); }
  };

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleList = (k, item) => setForm(p => {
    const l = p[k] || [];
    return { ...p, [k]: l.includes(item) ? l.filter(i => i !== item) : [...l, item] };
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('wunddok_token');
      await axios.post(`${API}/api/anamnese`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Anamnese gespeichert');
      navigate(`/patienten/${patientId}`);
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const bmi = form.ernaehrungszustand_groesse > 0
    ? Math.round((form.ernaehrungszustand_gewicht / Math.pow(form.ernaehrungszustand_groesse / 100, 2)) * 10) / 10
    : 0;

  const ToggleBtns = ({ options, selected, onToggle }) => (
    <div className="flex flex-wrap gap-2">
      {options.map(o => (
        <button key={o} type="button" onClick={() => onToggle(o)}
          className={`px-3 py-2 rounded-lg text-sm font-medium border transition-all min-h-[44px] ${
            selected.includes(o) ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          }`} data-testid={`anamnese-${o.replace(/\s/g, '-').toLowerCase()}`}>{o}</button>
      ))}
    </div>
  );

  if (loading) return <div className="p-6 flex justify-center"><div className="w-8 h-8 border-3 border-teal-200 border-t-teal-700 rounded-full animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="anamnese-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 rounded-xl" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <h1 className="text-xl font-heading font-bold text-slate-900">Anamnese</h1>
      </div>

      {/* Grunderkrankungen */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Heart className="w-5 h-5 text-teal-700" />Grunderkrankungen</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ToggleBtns options={GRUNDERKRANKUNGEN} selected={form.grunderkrankungen} onToggle={o => toggleList('grunderkrankungen', o)} />
          <div className="space-y-2"><Label>Sonstige</Label>
            <Input data-testid="grunderkrankungen-sonstige" value={form.grunderkrankungen_sonstige} onChange={e => update('grunderkrankungen_sonstige', e.target.value)} placeholder="Weitere Erkrankungen..." className="h-12" /></div>
        </CardContent>
      </Card>

      {/* Allgemeinzustand */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-base">Allgemeinzustand</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>Gewicht (kg)</Label>
              <Input data-testid="gewicht-input" type="number" step="0.1" value={form.ernaehrungszustand_gewicht || ''} onChange={e => update('ernaehrungszustand_gewicht', parseFloat(e.target.value) || 0)} className="h-12" placeholder="kg" /></div>
            <div className="space-y-2"><Label>Größe (cm)</Label>
              <Input data-testid="groesse-input" type="number" value={form.ernaehrungszustand_groesse || ''} onChange={e => update('ernaehrungszustand_groesse', parseFloat(e.target.value) || 0)} className="h-12" placeholder="cm" /></div>
            <div className="space-y-2"><Label>BMI</Label>
              <div className={`h-12 rounded-md border flex items-center justify-center font-bold text-lg ${bmi > 30 ? 'bg-rose-50 text-rose-700 border-rose-200' : bmi > 25 ? 'bg-amber-50 text-amber-700 border-amber-200' : bmi > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-400 border-slate-200'}`} data-testid="bmi-display">{bmi > 0 ? bmi : '-'}</div></div>
          </div>
          <div className="space-y-2"><Label>Mobilität</Label>
            <Select value={form.mobilitaet} onValueChange={v => update('mobilitaet', v)}>
              <SelectTrigger className="h-12" data-testid="mobilitaet-select"><SelectValue placeholder="Mobilität" /></SelectTrigger>
              <SelectContent>
                {['Vollständig mobil', 'Leicht eingeschränkt', 'Stark eingeschränkt', 'Bettlägerig', 'Rollstuhlpflichtig'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Allergien</Label>
            <Textarea data-testid="allergien-input" value={form.allergien} onChange={e => update('allergien', e.target.value)} placeholder="Bekannte Allergien..." rows={2} /></div>
          <div className="space-y-2"><Label>Medikation (insb. Antikoagulantien, Immunsuppressiva)</Label>
            <Textarea data-testid="medikation-input" value={form.medikation} onChange={e => update('medikation', e.target.value)} placeholder="Aktuelle Medikation..." rows={2} /></div>
        </CardContent>
      </Card>

      {/* Psychosoziale Faktoren (DNQP 2025) */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><Brain className="w-5 h-5 text-teal-700" />Psychosoziale Faktoren (DNQP 2025)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2"><Label>Lebensqualität</Label>
            <Select value={form.psychosozial_lebensqualitaet} onValueChange={v => update('psychosozial_lebensqualitaet', v)}>
              <SelectTrigger className="h-12" data-testid="lebensqualitaet-select"><SelectValue placeholder="Einschätzung" /></SelectTrigger>
              <SelectContent>{['Gut', 'Mäßig', 'Eingeschränkt', 'Stark eingeschränkt'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Körperbild / Belastungserleben</Label>
            <Select value={form.psychosozial_koerperbild} onValueChange={v => update('psychosozial_koerperbild', v)}>
              <SelectTrigger className="h-12" data-testid="koerperbild-select"><SelectValue placeholder="Einschätzung" /></SelectTrigger>
              <SelectContent>{['Unbelastet', 'Leicht belastet', 'Mäßig belastet', 'Stark belastet'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Soziale Unterstützung</Label>
            <Select value={form.psychosozial_unterstuetzung} onValueChange={v => update('psychosozial_unterstuetzung', v)}>
              <SelectTrigger className="h-12" data-testid="unterstuetzung-select"><SelectValue placeholder="Einschätzung" /></SelectTrigger>
              <SelectContent>{['Sehr gut (Familie/Angehörige)', 'Gut', 'Gering', 'Keine', 'Professionell'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-2"><Label>Krankheitsverständnis (Pflichtfeld DNQP)</Label>
            <Textarea data-testid="krankheitsverstaendnis-input" value={form.psychosozial_krankheitsverstaendnis} onChange={e => update('psychosozial_krankheitsverstaendnis', e.target.value)} placeholder="Was versteht der Patient über seine Erkrankung und Wunde?" rows={3} /></div>
          <div className="space-y-2"><Label>Selbstmanagement-Fähigkeiten</Label>
            <Select value={form.psychosozial_selbstmanagement} onValueChange={v => update('psychosozial_selbstmanagement', v)}>
              <SelectTrigger className="h-12" data-testid="selbstmanagement-select"><SelectValue placeholder="Einschätzung" /></SelectTrigger>
              <SelectContent>{['Vollständig selbstständig', 'Mit Anleitung möglich', 'Eingeschränkt', 'Nicht möglich'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select></div>
        </CardContent>
      </Card>

      {/* Rezidiv-Risikofaktoren */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3"><CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-600" />Rezidiv-Risikofaktoren</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <ToggleBtns options={REZIDIV_RISIKEN} selected={form.rezidiv_risikofaktoren} onToggle={o => toggleList('rezidiv_risikofaktoren', o)} />
          <div className="space-y-2"><Label>Sonstige Risiken</Label>
            <Input data-testid="rezidiv-sonstige" value={form.rezidiv_sonstige} onChange={e => update('rezidiv_sonstige', e.target.value)} placeholder="Weitere Risikofaktoren..." className="h-12" /></div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl" data-testid="save-anamnese-btn">
        <Save className="w-4 h-4 mr-2" />{saving ? 'Wird gespeichert...' : 'Anamnese speichern'}
      </Button>
    </div>
  );
}
